import { getFormProps, getInputProps, useForm } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import { type SEOHandle } from '@nasa-gcn/remix-seo'
import { data, redirect, Form, useSearchParams } from 'react-router'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { z } from 'zod'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { ErrorList, Field } from '#app/components/forms.tsx'
import { Spacer } from '#app/components/spacer.tsx'
import { StatusButton } from '#app/components/ui/status-button.tsx'
import { requireAnonymous } from '#app/utils/auth.server.ts'
import { getAppName } from '#app/utils/branding.ts'
import { checkHoneypot } from '#app/utils/honeypot.server.ts'
import { getTranslator } from '#app/utils/i18n.server.ts'
import { useTranslation, type Translator } from '#app/utils/i18n.ts'
import { useIsPending } from '#app/utils/misc.tsx'
import { createEmailSchema } from '#app/utils/user-validation.ts'
import { type Route } from './+types/login.ts'
import { sendLoginCode } from './login-code.server.tsx'
import { redirectToQueryParam } from './verify.tsx'

export const handle: SEOHandle = {
	getSitemapEntries: () => null,
}

// schema factory: zod messages resolve in the caller's locale — client passes
// `useTranslation()`, the action passes `getTranslator(request)`
const LoginFormSchema = (t: Translator) =>
	z.object({
		email: createEmailSchema(t),
		redirectTo: z.string().optional(),
	})

export async function loader({ request }: Route.LoaderArgs) {
	await requireAnonymous(request)
	return {}
}

export async function action({ request }: Route.ActionArgs) {
	await requireAnonymous(request)
	const t = getTranslator(request)
	const formData = await request.formData()
	await checkHoneypot(formData)
	const submission = parseWithZod(formData, { schema: LoginFormSchema(t) })
	if (submission.status !== 'success') {
		return data(
			{ result: submission.reply() },
			{ status: submission.status === 'error' ? 400 : 200 },
		)
	}

	// anti-enumeration (§ 1.1): NO user lookup here — existing and new emails
	// take the identical path (send code → verify page); the existing/new
	// branching happens server-side only AFTER the code is verified.
	// `createEmailSchema` has already lowercased the email, so the canonical
	// form becomes the Verification.target (§ 1.2).
	const { email, redirectTo } = submission.value

	// The send is fire-and-forget (see `sendLoginCode`): EVERY successful
	// submission returns the identical redirect — a failed send included. That
	// uniformity is deliberate on two counts: the user's wait no longer rides
	// the Resend round-trip, and the response stays free of any send-derived
	// signal (the anti-enumeration discipline above). A send that fails is
	// diagnosed from the server log; the user's recovery path is the "resend"
	// affordance on the verify page.
	const { verifyRedirect } = await sendLoginCode({ request, email })

	if (redirectTo) {
		verifyRedirect.searchParams.set(redirectToQueryParam, redirectTo)
	}
	return redirect(verifyRedirect.toString())
}

export default function LoginPage({ actionData }: Route.ComponentProps) {
	const t = useTranslation()
	const isPending = useIsPending()
	const [searchParams] = useSearchParams()
	const redirectTo = searchParams.get('redirectTo')

	const [form, fields] = useForm({
		id: 'login-form',
		constraint: getZodConstraint(LoginFormSchema(t)),
		defaultValue: { redirectTo },
		lastResult: actionData?.result,
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: LoginFormSchema(t) })
		},
		shouldRevalidate: 'onBlur',
	})

	return (
		<div className="flex min-h-full flex-col justify-center pt-20 pb-32">
			<div className="mx-auto w-full max-w-md">
				<div className="flex flex-col gap-3 text-center">
					<h1 className="text-h1">{t('auth.loginTitle')}</h1>
					<p className="text-body-md text-muted-foreground">
						{t('auth.loginSubtitle')}
					</p>
				</div>
				<Spacer size="xs" />
				<div className="mx-auto w-full max-w-md px-8">
					<Form method="POST" {...getFormProps(form)}>
						<HoneypotInputs />
						<Field
							labelProps={{
								htmlFor: fields.email.id,
								children: t('auth.email'),
							}}
							inputProps={{
								...getInputProps(fields.email, { type: 'email' }),
								autoFocus: true,
								autoComplete: 'email',
								className: 'lowercase',
							}}
							errors={fields.email.errors}
						/>
						<input {...getInputProps(fields.redirectTo, { type: 'hidden' })} />
						<ErrorList errors={form.errors} id={form.errorId} />
						<StatusButton
							className="w-full"
							status={isPending ? 'pending' : (form.status ?? 'idle')}
							type="submit"
							disabled={isPending}
						>
							{t('auth.sendCode')}
						</StatusButton>
					</Form>
				</div>
			</div>
		</div>
	)
}

export const meta: Route.MetaFunction = () => {
	return [{ title: `Вхід | ${getAppName()}` }]
}

export function ErrorBoundary() {
	return <GeneralErrorBoundary />
}
